import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
  Avatar,
  Space,
  Tabs,
  Row,
  Col,
  Statistic,
  Tooltip,
  Typography,
  Upload,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  VideoCameraOutlined,
  UploadOutlined,
  ImportOutlined,
  ReloadOutlined,
  DownOutlined,
  EyeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import TikTokVideosDetail from './TikTokVideosDetail';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Text } = Typography;

const TikTokManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showVideosDetail, setShowVideosDetail] = useState(false);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('accounts');
  const [batchUsernames, setBatchUsernames] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [useAsyncBatch, setUseAsyncBatch] = useState(false);

  // Function to extract username from TikTok URL or @username
  const extractUsername = (input) => {
    const trimmed = input.trim();
    
    // If it's already a clean username (no @, no URL)
    if (!trimmed.includes('@') && !trimmed.includes('tiktok.com')) {
      return trimmed;
    }
    
    // If it's @username format
    if (trimmed.startsWith('@')) {
      return trimmed.substring(1);
    }
    
    // If it's a TikTok URL
    if (trimmed.includes('tiktok.com/@')) {
      const match = trimmed.match(/tiktok\.com\/@([^\/\?]+)/);
      if (match) {
        return match[1];
      }
    }
    
    // If it's a full URL with @
    if (trimmed.includes('@')) {
      const match = trimmed.match(/@([^\/\?]+)/);
      if (match) {
        return match[1];
      }
    }
    
    // Return as is if no pattern matches
    return trimmed;
  };

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/tiktok-management/accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      message.error('Failed to load TikTok accounts');
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleAddAccount = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleBatchImport = () => {
    batchForm.resetFields();
    setBatchUsernames('');
    setBatchModalVisible(true);
  };

  const handleBatchUsernamesChange = (e) => {
    const value = e.target.value;
    setBatchUsernames(value);
    
    // Count usernames
    const usernameLines = value.split('\n').filter(line => line.trim() !== '');
    const usernameCount = usernameLines.length;
    
    // Update form field for validation
    batchForm.setFieldsValue({ usernames: value });
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setBatchUsernames(content);
      batchForm.setFieldsValue({ usernames: content });
    };
    reader.readAsText(file);
    return false; // Prevent upload
  };

  const handleBatchSubmit = async (values) => {
    try {
      const rawUsernames = values.usernames.split('\n').filter(line => line.trim() !== '');
      
      if (rawUsernames.length === 0) {
        message.error('Vui lòng nhập ít nhất một username');
        return;
      }

      // Extract usernames from URLs or @usernames
      const usernames = rawUsernames.map(extractUsername).filter(username => username.trim() !== '');
      
      if (usernames.length === 0) {
        message.error('Không tìm thấy username hợp lệ');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Choose endpoint based on useAsyncBatch
      const endpoint = useAsyncBatch 
        ? '/api/tiktok-management/batch-import-async'
        : '/api/tiktok-management/batch-import';
      
      // Send batch request
      const response = await axios.post(endpoint, {
        usernames: usernames
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (useAsyncBatch) {
          // Async import response
          const { task_id, total_accounts, estimated_time } = response.data;
          message.success(`Đã bắt đầu import ${total_accounts} usernames trong background. Task ID: ${task_id} (Ước tính: ${estimated_time})`);
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchUsernames('');
          // Don't reload immediately for async import
          setTimeout(() => loadAccounts(), 5000); // Reload after 5 seconds
        } else {
          // Sync import response
          const { success_count, error_count, errors } = response.data;
          
          if (error_count > 0) {
            message.warning(`Import hoàn thành: ${success_count} thành công, ${error_count} lỗi`);
            if (errors && errors.length > 0) {
              console.log('Import errors:', errors);
            }
          } else {
            message.success(`Đã import thành công ${success_count} usernames`);
          }
          
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchUsernames('');
          loadAccounts(); // Reload accounts
        }
      } else {
        message.error(response.data.message || 'Có lỗi xảy ra khi import');
      }
    } catch (error) {
      console.error('Error batch importing:', error);
      message.error('Có lỗi xảy ra khi import usernames: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAccount = async (account) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Call API to get fresh account info
      const response = await axios.get(`/api/tiktok-management/accounts/${account.id}/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const updatedAccount = response.data.account;
        
        // Show account info in a modal
        Modal.info({
          title: `Account Info: @${updatedAccount.username}`,
          width: 600,
          content: (
            <div>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <Avatar
                      src={updatedAccount.avatar_url}
                      icon={<UserOutlined />}
                      size={80}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Text strong>@{updatedAccount.username}</Text>
                    </div>
                  </div>
                </Col>
                <Col span={16}>
                  <div>
                    <p><strong>Nickname:</strong> {updatedAccount.nickname || 'N/A'}</p>
                    <p><strong>Followers:</strong> {updatedAccount.follower_count?.toLocaleString() || 0}</p>
                    <p><strong>Following:</strong> {updatedAccount.following_count?.toLocaleString() || 0}</p>
                    <p><strong>Videos:</strong> {updatedAccount.video_count?.toLocaleString() || 0}</p>
                    <p><strong>Likes:</strong> {updatedAccount.like_count?.toLocaleString() || 0}</p>
                    <p><strong>Status:</strong> 
                      <Tag color={updatedAccount.is_active ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                        {updatedAccount.is_active ? 'Active' : 'Inactive'}
                      </Tag>
                    </p>
                    {updatedAccount.bio && (
                      <p><strong>Bio:</strong> {updatedAccount.bio}</p>
                    )}
                    <p><strong>Last Updated:</strong> {new Date(updatedAccount.updated_at).toLocaleString()}</p>
                  </div>
                </Col>
              </Row>
            </div>
          ),
        });
        
        // Reload accounts to get updated data
        loadAccounts();
      } else {
        message.error(response.data.message || 'Failed to check account');
      }
    } catch (error) {
      console.error('Error checking account:', error);
      message.error('Failed to check account: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/tiktok-management/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Account deleted successfully');
      loadAccounts();
    } catch (error) {
      message.error('Failed to delete account');
      console.error('Error deleting account:', error);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một account để xóa');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      // Xóa từng account đã chọn
      const deletePromises = selectedRowKeys.map(accountId => 
        axios.delete(`/api/tiktok-management/accounts/${accountId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      const results = await Promise.allSettled(deletePromises);
      
      let successCount = 0;
      let errorCount = 0;
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successCount++;
        } else {
          errorCount++;
        }
      });

      if (successCount > 0) {
        message.success(`Đã xóa thành công ${successCount} account(s)`);
      }
      if (errorCount > 0) {
        message.error(`Không thể xóa ${errorCount} account(s)`);
      }

      // Clear selection and reload data
      setSelectedRowKeys([]);
      setSelectedRows([]);
      loadAccounts();
      
    } catch (error) {
      console.error('Error deleting selected accounts:', error);
      message.error('Lỗi khi xóa accounts đã chọn: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAll = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.delete('/api/tiktok-management/accounts/delete-all', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success(response.data.message);
        // Clear selection and reload data
        setSelectedRowKeys([]);
        setSelectedRows([]);
        loadAccounts();
      } else {
        message.error(response.data.error || 'Lỗi khi xóa tất cả accounts');
      }
      
    } catch (error) {
      console.error('Error deleting all accounts:', error);
      message.error('Lỗi khi xóa tất cả accounts: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateAllAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Send batch update request
      const response = await axios.post('/api/tiktok-management/accounts/batch-update', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const { updated, error_count, errors } = response.data;
        
        if (updated > 0) {
          message.success(`Đã cập nhật thành công ${updated} accounts`);
          loadAccounts();
        } else if (error_count > 0) {
          message.error(`Có lỗi khi cập nhật ${error_count} accounts`);
        } else {
          message.info('Không có account nào cần cập nhật');
        }
        
        // Show detailed errors if any
        if (errors && errors.length > 0) {
          console.warn('Update errors:', errors);
        }
      } else {
        message.error(response.data.message || 'Có lỗi xảy ra khi cập nhật');
      }
    } catch (error) {
      message.error('Lỗi khi cập nhật tất cả accounts: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = async (values) => {
    try {
      const token = localStorage.getItem('access_token');
      // Add new account
        // Lấy thông tin từ TikTok API trước khi tạo account
        message.loading('Đang lấy thông tin từ TikTok...', 0);
        
        // Sử dụng endpoint auto-fetch mới
        await axios.post('/api/tiktok-management/accounts/auto-fetch', null, {
          params: { username: values.username },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        message.destroy();
        message.success(`Đã thêm tài khoản @${values.username} thành công!`);
      setModalVisible(false);
      loadAccounts();
    } catch (error) {
      message.destroy();
      message.error('Failed to save account');
      console.error('Error saving account:', error);
    }
  };


  const handleViewVideos = (account) => {
    setSelectedAccount(account);
    setShowVideosDetail(true);
  };

  const handleBackFromVideos = () => {
    setShowVideosDetail(false);
    setSelectedAccount(null);
  };


  const accountColumns = [
    {
      title: 'Avatar',
      dataIndex: 'avatar_url',
      key: 'avatar',
      width: 80,
      render: (url) => (
        <Avatar
          src={url}
          icon={<UserOutlined />}
          size={40}
        />
      ),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text) => <strong>@{text}</strong>,
    },
    {
      title: 'Nickname',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: 'Followers',
      dataIndex: 'follower_count',
      key: 'followers',
      render: (count) => count?.toLocaleString() || 0,
    },
    {
      title: 'Videos',
      dataIndex: 'video_count',
      key: 'videos',
      render: (count) => count?.toLocaleString() || 0,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Check Account Info">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleCheckAccount(record)}
            >
              Check
            </Button>
          </Tooltip>
          <Tooltip title="View Videos Detail">
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              size="small"
              onClick={() => handleViewVideos(record)}
            >
              Videos
            </Button>
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this account?"
            onConfirm={() => handleDeleteAccount(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];


  // Nếu đang hiển thị trang videos detail
  if (showVideosDetail && selectedAccount) {
    return (
      <TikTokVideosDetail 
        account={selectedAccount} 
        onBack={handleBackFromVideos}
      />
    );
  }

  return (
    <div>
      <Card title="TikTok Management" extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleUpdateAllAccounts}
            loading={loading}
          >
            Update All
          </Button>
          <Button icon={<ImportOutlined />} onClick={handleBatchImport}>
            Batch Import
          </Button>
          {(selectedRowKeys.length > 0 || accounts.length > 0) && (
            <Dropdown
              menu={{
                items: [
                  ...(selectedRowKeys.length > 0 ? [{
                    key: 'delete-selected',
                    label: (
                      <Popconfirm
                        title={`Xóa ${selectedRowKeys.length} account đã chọn?`}
                        onConfirm={handleDeleteSelected}
                        okText="Xóa"
                        cancelText="Hủy"
                        placement="left"
                      >
                        <span>Xóa đã chọn ({selectedRowKeys.length})</span>
                      </Popconfirm>
                    ),
                    icon: <DeleteOutlined />
                  }] : []),
                  ...(accounts.length > 0 ? [{
                    key: 'delete-all',
                    label: (
                      <Popconfirm
                        title={`Xóa TẤT CẢ ${accounts.length} accounts và videos? Hành động này không thể hoàn tác!`}
                        onConfirm={handleDeleteAll}
                        okText="Xóa tất cả"
                        cancelText="Hủy"
                        placement="left"
                      >
                        <span>Xóa tất cả ({accounts.length})</span>
                      </Popconfirm>
                    ),
                    icon: <DeleteOutlined />
                  }] : [])
                ]
              }}
              trigger={['click']}
            >
              <Button danger icon={<DeleteOutlined />}>
                Xóa <DownOutlined />
              </Button>
            </Dropdown>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAccount}>
            Add Account
          </Button>
        </Space>
      }>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="TikTok Accounts" key="accounts">
            <Table
              columns={accountColumns}
              dataSource={accounts}
              rowKey="id"
              loading={loading}
              rowSelection={{
                selectedRowKeys: selectedRowKeys,
                onChange: (selectedRowKeys, selectedRows) => {
                  setSelectedRowKeys(selectedRowKeys);
                  setSelectedRows(selectedRows);
                },
                getCheckboxProps: (record) => ({
                  name: record.username,
                }),
              }}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          
        </Tabs>
      </Card>

      {/* Account Modal */}
      <Modal
        title="Add TikTok Account"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAccountSubmit}
        >
          <Form.Item
            name="username"
            label="TikTok Username"
            rules={[{ required: true, message: 'Please enter TikTok username' }]}
          >
            <Input 
              placeholder="@username (without @ symbol)" 
              addonBefore="@"
            />
          </Form.Item>
          
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <Text type="secondary">
              💡 Chỉ cần nhập username, hệ thống sẽ tự động lấy thông tin từ TikTok API
            </Text>
          </div>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Add Account
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


      {/* Batch Import Modal */}
      <Modal
        title="Batch Import Usernames"
        open={batchModalVisible}
        onCancel={() => {
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchUsernames('');
        }}
        footer={null}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchSubmit}
        >
          <Form.Item
            name="usernames"
            label={<span style={{ color: 'red' }}>* Usernames, URLs, or @usernames (one per line)</span>}
            rules={[{ required: true, message: 'Vui lòng nhập ít nhất một username' }]}
          >
            <TextArea
              rows={8}
              placeholder="Enter usernames, URLs, or @usernames, one per line:&#10;Examples:&#10;@promeone&#10;https://www.tiktok.com/@promeone&#10;promeone&#10;@https://www.tiktok.com/@promeone"
              value={batchUsernames}
              onChange={handleBatchUsernamesChange}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          {/* Preview extracted usernames */}
          {batchUsernames && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <Text strong style={{ color: '#1890ff' }}>📋 Extracted Usernames Preview:</Text>
              <div style={{ marginTop: 8 }}>
                {batchUsernames.split('\n').filter(line => line.trim() !== '').map((line, index) => {
                  const extracted = extractUsername(line);
                  const isChanged = extracted !== line.trim();
                  return (
                    <div key={index} style={{ marginBottom: 4, fontSize: '12px' }}>
                      <Text type="secondary" style={{ textDecoration: isChanged ? 'line-through' : 'none' }}>
                        {line.trim()}
                      </Text>
                      {isChanged && (
                        <>
                          <Text> → </Text>
                          <Text strong style={{ color: '#52c41a' }}>{extracted}</Text>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Or upload a text file:</Text>
            <Upload
              beforeUpload={handleFileUpload}
              accept=".txt"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} style={{ marginLeft: 8 }}>
                Upload TXT File
              </Button>
            </Upload>
          </div>

          <Form.Item style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useAsyncBatch}
                onChange={(e) => setUseAsyncBatch(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <Text>Background Import (cho số lượng lớn, chạy ngầm)</Text>
            </label>
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 24 }}>
              {useAsyncBatch 
                ? 'Import sẽ chạy trong background, bạn có thể đóng modal và làm việc khác'
                : 'Import sẽ chạy đồng bộ, bạn cần chờ kết quả'
              }
            </Text>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={loading}
              >
                {useAsyncBatch ? 'Start Background Import' : 'Import'} {
                  batchUsernames 
                    ? batchUsernames.split('\n').filter(line => line.trim() !== '').map(extractUsername).filter(username => username.trim() !== '').length
                    : 0
                } Usernames
              </Button>
              <Button onClick={() => {
                setBatchModalVisible(false);
                batchForm.resetFields();
                setBatchUsernames('');
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TikTokManagement;